import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Send, X, Loader2, BookOpen } from 'lucide-react';
import { blogPosts } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const suggestedQuestions = [
  'What is the EA course and who can enroll?',
  'How long does it take to become an EA?',
  'What is a PTIN and how do I get one?',
  'EA vs CPA — which is better for me?',
  'How much can I earn as an EA in India?',
];

const AIChatDrawer = ({ open, onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        'Hi! I’m your Eduvantage EA Assistant. Ask me anything about the Enrolled Agent course, SEE exams, PTIN, or careers in US taxation.',
      related_slugs: [],
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const newUserMsg = { role: 'user', content };
    const nextMessages = [...messages, newUserMsg];
    setMessages([...nextMessages, { role: 'assistant', content: '', related_slugs: [], _pending: true }]);
    setInput('');
    setLoading(true);

    try {
      const payloadMessages = nextMessages.map(({ role, content }) => ({ role, content }));
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        messages: payloadMessages,
      });
      setSessionId(data.session_id);
      setMessages((prev) => {
        const copy = [...prev];
        // remove pending placeholder
        copy.pop();
        return [
          ...copy,
          { role: 'assistant', content: data.reply, related_slugs: data.related_slugs || [] },
        ];
      });
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        copy.pop();
        return [
          ...copy,
          {
            role: 'assistant',
            content:
              'Sorry, I ran into an issue reaching my knowledge base. Please try again in a moment, or [contact a counselor](/contact).',
            related_slugs: [],
          },
        ];
      });
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdownLite = (text) => {
    // Very light markdown: **bold**, [label](/url), and newlines
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      const parts = [];
      const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
      let lastIndex = 0;
      let m;
      while ((m = regex.exec(line)) !== null) {
        if (m.index > lastIndex) parts.push(line.slice(lastIndex, m.index));
        if (m[1] !== undefined) {
          parts.push(
            <strong key={`b-${i}-${m.index}`} className="text-[#143324]">
              {m[1]}
            </strong>
          );
        } else if (m[2] && m[3]) {
          const url = m[3];
          const isInternal = url.startsWith('/');
          if (isInternal) {
            parts.push(
              <Link
                key={`l-${i}-${m.index}`}
                to={url}
                onClick={onClose}
                className="text-[#143324] underline decoration-[#143324]/40 underline-offset-2 hover:decoration-[#143324]"
              >
                {m[2]}
              </Link>
            );
          } else {
            parts.push(
              <a
                key={`l-${i}-${m.index}`}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-[#143324] underline decoration-[#143324]/40 underline-offset-2 hover:decoration-[#143324]"
              >
                {m[2]}
              </a>
            );
          }
        }
        lastIndex = m.index + m[0].length;
      }
      if (lastIndex < line.length) parts.push(line.slice(lastIndex));
      return (
        <p key={i} className="mb-2 last:mb-0">
          {parts}
        </p>
      );
    });
  };

  const suggestedBlogs = (slugs = []) =>
    slugs.map((s) => blogPosts.find((p) => p.slug === s)).filter(Boolean);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-[#0f2a1e]/40 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* Drawer */}
      <aside
        className={`fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-[#f5f1e6] border-l border-[#143324]/10 shadow-2xl z-[61] flex flex-col transition-transform duration-400 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#143324]/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#143324] text-[#f5f1e6] flex items-center justify-center relative">
              <Sparkles size={16} />
              <span className="absolute -bottom-0 -right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-[#f5f1e6]" />
            </div>
            <div>
              <p className="font-body font-semibold text-[#143324] text-[15px]">EA Assistant</p>
              <p className="text-[11px] text-[#3d3d3d] font-body">Powered by DeepSeek AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#143324]/8 flex items-center justify-center text-[#143324]"
            aria-label="Close chat"
          >
            <X size={16} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
          {messages.map((m, i) => {
            if (m.role === 'user') {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] bg-[#143324] text-[#f5f1e6] rounded-2xl rounded-tr-md px-4 py-3 text-[14px] font-body leading-relaxed">
                    {m.content}
                  </div>
                </div>
              );
            }
            const relatedPosts = suggestedBlogs(m.related_slugs || []);
            return (
              <div key={i} className="flex justify-start">
                <div className="max-w-[90%]">
                  <div className="bg-white border border-[#143324]/8 rounded-2xl rounded-tl-md px-4 py-3 text-[14px] font-body text-[#1c1c1c] leading-relaxed">
                    {m._pending ? (
                      <span className="inline-flex items-center gap-2 text-[#3d3d3d]">
                        <Loader2 size={14} className="animate-spin" /> Thinking…
                      </span>
                    ) : (
                      renderMarkdownLite(m.content)
                    )}
                  </div>
                  {relatedPosts.length > 0 && !m._pending && (
                    <div className="mt-3">
                      <p className="text-[10.5px] tracking-wider uppercase text-[#3d3d3d] font-body mb-2 flex items-center gap-1.5">
                        <BookOpen size={11} /> Related articles
                      </p>
                      <div className="space-y-1.5">
                        {relatedPosts.map((p) => (
                          <Link
                            key={p.slug}
                            to={`/blog/${p.slug}`}
                            onClick={onClose}
                            className="block px-3 py-2 rounded-lg bg-white/70 border border-[#143324]/10 text-[12.5px] font-body text-[#143324] hover:bg-white transition-colors"
                          >
                            {p.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Suggestions (when only greeting) */}
        {messages.length <= 1 && (
          <div className="px-5 pb-3">
            <p className="text-[10.5px] tracking-wider uppercase text-[#3d3d3d] font-body mb-2">
              Try asking
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/70 text-[11.5px] font-body text-[#1c1c1c] hover:bg-white transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="px-5 py-4 border-t border-[#143324]/10 bg-[#f5f1e6]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about EA, PTIN, exams, salary…"
              className="flex-1 px-4 py-2.5 rounded-full border border-[#143324]/15 bg-white text-[14px] font-body text-[#1c1c1c] outline-none focus:border-[#143324]/40"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-11 h-11 rounded-full bg-[#143324] text-[#f5f1e6] flex items-center justify-center disabled:opacity-50 hover:bg-[#1c4732] transition-colors"
              aria-label="Send"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
          <p className="text-[10.5px] text-[#3d3d3d]/70 font-body mt-2 text-center">
            AI can make mistakes. Verify important dates with a counselor.
          </p>
        </div>
      </aside>
    </>
  );
};

export default AIChatDrawer;
